import { ReactElement, useCallback, useMemo, useState } from 'react';
import {
  DialogTitle,
  Stack,
  DialogContentText,
  TextField,
  Typography,
  Divider,
  CardHeader,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useFormik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import { sumBy } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { useDownloadSettlementPDF } from '~hooks/useDownloadSettlementPDF';
import { generateSettlementBillingItem } from '~services/billingItem';
import { hasError } from '~utils/formHelpers';
import { formatCurrencyValue } from '~utils/formatter';
import {
  SettlementBillingItemGenerateBody,
  SettlementBillingItemGenerateResponse,
} from '~services/billingItem/types';

import { useDialogGenerateLiquidationContext } from './Context';
import { FIELDS_NAME, getInitialValues, validationSchema } from './utils';
import {
  DialogGenerateLiquidationContentContainer,
  DialogGenerateLiquidationSummaryCard,
  DialogGenerateLiquidationGenerateDataCard,
  DialogGenerateLiquidationActions,
  DialogGenerateLiquidationActionsButton,
  DialogGenerateLiquidationActionsButtonLoading,
} from './styles';

const DialogGenerateLiquidationContent = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const { onClose, metadata, liquidationData, onSuccesGenerate } =
    useDialogGenerateLiquidationContext();

  const [settlementBillingId, setSettlementBillingId] =
    useState<Nullable<string>>(null);
  const [isCompletedRequest, setIsCompletedRequest] = useState(false);

  const [
    ,
    loadingGenerateSettlementBillingItem,
    ,
    executeGenerateSettlementBillingItem,
  ] = useLazyRequest({
    request: generateSettlementBillingItem,
    withPostSuccess: (response) => {
      const settlementBillingItemData = response.data
        ?.data as SettlementBillingItemGenerateResponse;

      setSettlementBillingId(settlementBillingItemData.settlementBillingItemId);
      setIsCompletedRequest(true);

      onSuccesGenerate();
    },
    withPostFailure: () => {
      enqueueSnackbar('Ha ocurrido un error, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const onSubmit = useCallback(
    async (
      values: ReturnType<typeof getInitialValues>,
      { setSubmitting }: FormikHelpers<ReturnType<typeof getInitialValues>>,
    ) => {
      const billingItemIds = liquidationData.billingItems.map(
        (billingItem) => billingItem.billingItemId,
      );
      const lot = Number(values[FIELDS_NAME.BATCH_NUMBER]);
      const adjustment = values[FIELDS_NAME.ADJUSTMENT_AMOUNT]
        ? Number(values[FIELDS_NAME.ADJUSTMENT_AMOUNT])
        : null;

      const adjustmentConcept = values[FIELDS_NAME.ADJUSTMENT_CONCEPT] || null;
      const footerNotes = values[FIELDS_NAME.LEGAL_NOTE] || null;

      const body: SettlementBillingItemGenerateBody = {
        billingItemIds,
        lot,
        adjustment,
        adjustmentConcept,
        footerNotes,
        giverCompanyId: liquidationData.giverCompanyId,
        companyIntegrationStoreId: liquidationData.companyIntegrationStoreId,
      };

      await executeGenerateSettlementBillingItem(body);

      setSubmitting(false);
    },
    [executeGenerateSettlementBillingItem, liquidationData],
  );

  const {
    values,
    touched,
    errors,
    getFieldProps,
    submitForm,
    isSubmitting,
    isValid,
  } = useFormik({
    initialValues: getInitialValues(metadata),
    validationSchema,
    onSubmit,
    validateOnMount: true,
  });

  const subTotalValue = useMemo(
    () => sumBy(liquidationData.billingItems, 'shipPrice'),
    [liquidationData.billingItems],
  );

  const adjustemAmountValue = useMemo(
    () => Number(values[FIELDS_NAME.ADJUSTMENT_AMOUNT]),
    [values],
  );

  const totalValue = useMemo(
    () => subTotalValue + adjustemAmountValue,
    [adjustemAmountValue, subTotalValue],
  );

  const loadingSubmit = useMemo(
    () => loadingGenerateSettlementBillingItem || isSubmitting,
    [loadingGenerateSettlementBillingItem, isSubmitting],
  );

  const {
    loading: loadingDownloadSettlementPDF,
    onDownloadPDF: executeDownloadSettlementPDF,
  } = useDownloadSettlementPDF();

  const handleDownloadSettlementPDF = useCallback(async () => {
    if (settlementBillingId) {
      await executeDownloadSettlementPDF(settlementBillingId);
    }
  }, [executeDownloadSettlementPDF, settlementBillingId]);

  return (
    <>
      <DialogTitle>Generar liquidación</DialogTitle>

      <DialogGenerateLiquidationContentContainer dividers>
        <Stack spacing={2}>
          <DialogContentText variant="body2" color="text.secondary">
            Revise los detalles para confeccionar la liquidación del lote de
            viajes seleccionados. Los viajes liquidados se quitarán de la lista
            de pendientes tras ser liquidados.
          </DialogContentText>

          <Stack spacing={4} direction="row">
            <Stack spacing={2} flex={1}>
              <TextField
                label="Código de lote"
                fullWidth
                autoComplete="off"
                type="number"
                error={hasError(touched, errors, FIELDS_NAME.BATCH_NUMBER)}
                {...getFieldProps(FIELDS_NAME.BATCH_NUMBER)}
                disabled={Boolean(metadata.lotCounter) || isCompletedRequest}
              />

              <Stack spacing={1}>
                <Stack>
                  <DialogContentText
                    variant="body2"
                    color="text.primary"
                    fontWeight="bold"
                  >
                    Ajustes
                  </DialogContentText>

                  <DialogContentText variant="caption" color="text.secondary">
                    Utiliza un ajuste positivo o negativo para alterar la
                    liquidación a enviar.
                  </DialogContentText>
                </Stack>

                <TextField
                  label="Concepto ajuste (Opcional)"
                  fullWidth
                  autoComplete="off"
                  multiline
                  rows={2}
                  error={hasError(
                    touched,
                    errors,
                    FIELDS_NAME.ADJUSTMENT_CONCEPT,
                  )}
                  {...getFieldProps(FIELDS_NAME.ADJUSTMENT_CONCEPT)}
                  disabled={isCompletedRequest}
                />

                <TextField
                  label="Importe ajuste (Opcional)"
                  fullWidth
                  autoComplete="off"
                  type="number"
                  error={hasError(
                    touched,
                    errors,
                    FIELDS_NAME.ADJUSTMENT_AMOUNT,
                  )}
                  {...getFieldProps(FIELDS_NAME.ADJUSTMENT_AMOUNT)}
                  disabled={isCompletedRequest}
                />
              </Stack>

              <Stack spacing={1}>
                <Stack>
                  <DialogContentText
                    variant="body2"
                    color="text.primary"
                    fontWeight="bold"
                  >
                    Notal al pie
                  </DialogContentText>

                  <DialogContentText variant="caption" color="text.secondary">
                    Utiliza este campo para detallar notas al pie del documento.
                  </DialogContentText>
                </Stack>

                <TextField
                  label="Notas (Opcional)"
                  fullWidth
                  autoComplete="off"
                  multiline
                  rows={4}
                  error={hasError(touched, errors, FIELDS_NAME.LEGAL_NOTE)}
                  {...getFieldProps(FIELDS_NAME.LEGAL_NOTE)}
                  disabled={isCompletedRequest}
                />
              </Stack>
            </Stack>

            <Stack spacing={3} flex={0.9}>
              <Stack spacing={2}>
                <DialogContentText
                  variant="body2"
                  color="text.primary"
                  fontWeight="bold"
                >
                  Resumen de la liquidación
                </DialogContentText>

                <DialogGenerateLiquidationSummaryCard>
                  <Stack spacing={0.5}>
                    <Stack spacing={1} direction="row">
                      <Typography variant="body2" sx={{ minWidth: 110 }}>
                        Empresa
                      </Typography>

                      <Typography variant="body2" fontWeight="bold">
                        {liquidationData.name}
                      </Typography>
                    </Stack>

                    <Stack spacing={1} direction="row">
                      <Typography variant="body2" sx={{ minWidth: 110 }}>
                        Viajes a liquidar
                      </Typography>

                      <Typography variant="body2" fontWeight="bold">
                        {liquidationData.billingItems.length}
                      </Typography>
                    </Stack>

                    <Stack spacing={1} direction="row">
                      <Typography variant="body2" sx={{ minWidth: 110 }}>
                        Subtotal
                      </Typography>

                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrencyValue(subTotalValue, {
                          style: 'currency',
                          currency: 'USD',
                          currencyDisplay: 'narrowSymbol',
                          minimumFractionDigits: 2,
                        })}
                      </Typography>
                    </Stack>

                    <Stack spacing={1} direction="row">
                      <Typography variant="body2" sx={{ minWidth: 110 }}>
                        Ajustes
                      </Typography>

                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrencyValue(adjustemAmountValue, {
                          style: 'currency',
                          currency: 'USD',
                          currencyDisplay: 'narrowSymbol',
                          minimumFractionDigits: 2,
                        })}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider />

                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ minWidth: 110 }}
                    >
                      Total
                    </Typography>

                    <Typography variant="h6" fontWeight="bold">
                      {formatCurrencyValue(totalValue, {
                        style: 'currency',
                        currency: 'USD',
                        currencyDisplay: 'narrowSymbol',
                        minimumFractionDigits: 2,
                      })}
                    </Typography>
                  </Stack>
                </DialogGenerateLiquidationSummaryCard>
              </Stack>

              {isCompletedRequest && (
                <DialogGenerateLiquidationGenerateDataCard>
                  <CardHeader
                    avatar={<CheckCircleIcon fontSize="inherit" />}
                    title="Liquidación generada"
                  />
                </DialogGenerateLiquidationGenerateDataCard>
              )}
            </Stack>
          </Stack>
        </Stack>
      </DialogGenerateLiquidationContentContainer>

      <DialogGenerateLiquidationActions>
        <DialogGenerateLiquidationActionsButton
          color="secondary"
          onClick={onClose}
        >
          Cerrar
        </DialogGenerateLiquidationActionsButton>

        {!isCompletedRequest && (
          <DialogGenerateLiquidationActionsButtonLoading
            variant="contained"
            color="primary"
            onClick={submitForm}
            loading={loadingSubmit}
            disabled={!isValid}
          >
            Generar
          </DialogGenerateLiquidationActionsButtonLoading>
        )}

        {isCompletedRequest && (
          <DialogGenerateLiquidationActionsButtonLoading
            variant="contained"
            color="primary"
            onClick={handleDownloadSettlementPDF}
            loading={loadingDownloadSettlementPDF}
          >
            Descargar
          </DialogGenerateLiquidationActionsButtonLoading>
        )}
      </DialogGenerateLiquidationActions>
    </>
  );
};

export default DialogGenerateLiquidationContent;
